import { QueryClientProvider } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { store } from '@/app/store';
import type { RootState } from '@/app/store';
import { StartupLoader } from '@/components/loading/StartupLoader';
import { useAuthSessionRefresh } from '@/features/auth/hooks/useAuthSessionRefresh';
import { refreshAuthSession, shouldClearSessionOnRefreshFailure } from '@/features/auth/lib/sessionRefresh';
import { loginSuccess, logout } from '@/features/auth/store/authSlice';
import { TelemetryBootstrap } from '@/features/monitoring/components/TelemetryBootstrap';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { queryClient } from '@/lib/query-client';

let hasBootstrappedSession = false;

function ThemeSync() {
  const theme = useSelector((state: RootState) => state.ui.theme);
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };

    setSystemPrefersDark(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const isDark = theme === 'system' ? systemPrefersDark : theme === 'dark';
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle('dark', isDark);
    root.style.colorScheme = isDark ? 'dark' : 'light';
    body.style.colorScheme = isDark ? 'dark' : 'light';

    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', isDark ? '#020617' : '#f8fafc');
    }

    const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (appleStatusBarMeta) {
      appleStatusBarMeta.setAttribute('content', isDark ? 'black-translucent' : 'default');
    }
  }, [systemPrefersDark, theme]);

  return null;
}

function AuthSessionSync() {
  useAuthSessionRefresh();
  return null;
}

function AuthSessionBootstrap({ children }: PropsWithChildren) {
  const dispatch = useAppDispatch();
  const { isAuthenticated, refreshToken, rememberMe } = useSelector((state: RootState) => state.auth);
  const [isReady, setIsReady] = useState(() => !isAuthenticated || !refreshToken);

  useEffect(() => {
    let cancelled = false;

    if (hasBootstrappedSession) {
      setIsReady(true);
      return () => {
        cancelled = true;
      };
    }

    hasBootstrappedSession = true;

    if (!isAuthenticated || !refreshToken) {
      setIsReady(true);
      return () => {
        cancelled = true;
      };
    }

    setIsReady(false);

    void refreshAuthSession(refreshToken)
      .then(refreshed => {
        if (!cancelled) {
          dispatch(loginSuccess({ ...refreshed, rememberMe }));
        }
      })
      .catch(error => {
        if (!cancelled && shouldClearSessionOnRefreshFailure(error)) {
          dispatch(logout());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isAuthenticated, refreshToken, rememberMe]);

  if (!isReady) {
    return <StartupLoader title="Checking secure session" subtitle="Restoring your clinic workspace" />;
  }

  return (
    <>
      <AuthSessionSync />
      {children}
    </>
  );
}

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeSync />
          <TelemetryBootstrap />
          <AuthSessionBootstrap>{children}</AuthSessionBootstrap>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}
